import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getCompanyProfileAPI, updateCompanyProfileAPI } from '../../../Services/mockApi';
import { languagesSeed, timezonesSeed } from '../../../mockData/company/settings';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import { companyUrl } from '../../../utils/mhNav';
import styles from './GeneralProfile.module.scss';

export default function GeneralProfile() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { control, register, handleSubmit, reset } = useForm({
    defaultValues: {
      companyName: '',
      domain: '',
      timezone: 'Asia/Muscat',
      primaryLanguage: 'en',
      secondaryLanguage: '',
      supportContactEmail: '',
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCompanyProfileAPI();
      reset(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (values) => {
    await updateCompanyProfileAPI(values);
    toast.success('Profile saved');
    load();
  };

  if (error && !loading) {
    return (
      <div>
        <PageHeader title="General profile" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }]} />
        <p className="text-danger">Could not load profile.</p>
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="General profile" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Settings' }]} />
      {loading ? (
        <LoadingSkeleton rows={6} cols={2} />
      ) : (
        <Form onSubmit={handleSubmit(onSave)}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Company name *</Form.Label>
                <Form.Control {...register('companyName', { required: true })} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Domain *</Form.Label>
                <Form.Control {...register('domain', { required: true })} />
                <Form.Text className="text-warning">Changing domain may require reconnecting integrations.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Label>Timezone *</Form.Label>
              <Controller
                name="timezone"
                control={control}
                render={({ field }) => (
                  <Select options={timezonesSeed} value={timezonesSeed.find((t) => t.value === field.value)} onChange={(o) => field.onChange(o?.value)} />
                )}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Primary language</Form.Label>
              <Controller
                name="primaryLanguage"
                control={control}
                render={({ field }) => (
                  <Select options={languagesSeed} value={languagesSeed.find((t) => t.value === field.value)} onChange={(o) => field.onChange(o?.value)} />
                )}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Secondary language</Form.Label>
              <Controller
                name="secondaryLanguage"
                control={control}
                render={({ field }) => (
                  <Select isClearable options={languagesSeed} value={languagesSeed.find((t) => t.value === field.value) || null} onChange={(o) => field.onChange(o?.value || '')} />
                )}
              />
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Support contact email</Form.Label>
                <Form.Control type="email" {...register('supportContactEmail')} />
              </Form.Group>
            </Col>
          </Row>

          <Card className={`mt-4 ${styles.readonly}`}>
            <Card.Body>
              <Card.Title as="h6">Read-only</Card.Title>
              <Row>
                <Col md={4}>
                  <div className="small text-muted">Tenant code</div>
                  <div className="fw-medium">T-204</div>
                </Col>
                <Col md={4}>
                  <div className="small text-muted">Plan</div>
                  <div className="fw-medium">Business Plus</div>
                </Col>
                <Col md={4}>
                  <div className="small text-muted">Created</div>
                  <div className="fw-medium">2024-01-15</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Button type="submit" className="mt-3">Save</Button>
        </Form>
      )}
    </div>
  );
}
